export const SITE_URL = 'https://devssoft.com';
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export const SERVICE_OFFERINGS = [
  {
    id: 'software-empresarial',
    name: 'Software empresarial a medida',
    problem: 'Procesos críticos sostenidos por planillas, tareas manuales o sistemas rígidos.',
    audience: 'Empresas que necesitan adaptar la tecnología a su operación real.',
    features: ['Sistemas web', 'Aplicaciones móviles', 'Modernización', 'Integraciones'],
  },
  {
    id: 'inteligencia-empresarial',
    name: 'Inteligencia empresarial e IA aplicada',
    problem: 'Datos dispersos que dificultan el análisis y la toma de decisiones.',
    audience: 'Equipos que necesitan consultar, comparar y comprender su operación.',
    features: ['Analítica', 'Automatización', 'Visualización de datos', 'IA aplicada a procesos'],
  },
  {
    id: 'arquitectura-software',
    name: 'Arquitectura e infraestructura de software',
    problem: 'Sistemas difíciles de mantener, integrar o evolucionar a medida que cambia el negocio.',
    audience: 'Organizaciones que buscan una base técnica mantenible y preparada para crecer.',
    features: ['Arquitectura escalable', 'Seguridad desde el diseño', 'Continuidad', 'Acompañamiento'],
  },
];

export const SOFTWARE_PRODUCTS = [
  {
    name: 'CoreBase CRM',
    path: '/apps/corebase',
    category: 'BusinessApplication',
    audience: 'Negocios de servicios',
    description: 'Gestiona leads, ventas, clientes y seguimiento comercial.',
  },
  {
    name: 'Inventario Devssoft',
    path: '/apps/inventario',
    category: 'BusinessApplication',
    audience: 'Negocios con almacén e inventario',
    description: 'Controla existencias, entradas, salidas y valoración del stock.',
  },
  {
    name: 'POS Retail Ropa',
    path: '/apps/retail-pos',
    category: 'BusinessApplication',
    audience: 'Tiendas de ropa y comercios minoristas',
    description: 'Integra punto de venta, inventario y lectura de códigos de barras.',
  },
  {
    name: 'POS Comida Rápida',
    path: '/apps/fastfood',
    category: 'BusinessApplication',
    audience: 'Negocios de comida rápida',
    description: 'Registra ventas, emite tickets y permite analizar los productos vendidos.',
  },
  {
    name: 'Devssoft Chat',
    path: '/apps/devssoft-chat',
    category: 'BusinessApplication',
    audience: 'Usuarios de Inventario y POS Retail',
    description: 'Permite consultar y analizar datos del negocio con IA en modo de solo lectura.',
  },
];

export const FAQ_ENTRIES = [
  {
    question: '¿Necesito reemplazar todo mi sistema?',
    answer: 'No necesariamente. Podemos modernizar o integrar lo que ya funciona y reconstruir únicamente lo que frena tu operación. La decisión parte del diagnóstico.',
  },
  {
    question: '¿Cómo saben qué necesita mi empresa?',
    answer: 'Empezamos con tus procesos reales: dónde se pierde tiempo, qué depende de una persona y qué no puede fallar. A partir de eso definimos juntos el alcance y la arquitectura.',
  },
  {
    question: '¿Desarrollan aplicaciones web y móviles?',
    answer: 'Sí. Diseñamos aplicaciones web y móviles cuando son el medio adecuado para conectar equipos, procesos y clientes. El alcance se define según la operación y los objetivos del proyecto.',
  },
  {
    question: '¿Cómo aplican inteligencia artificial a un negocio?',
    answer: 'La incorporamos en casos concretos, como consulta de información, análisis o automatización. Primero verificamos que exista un problema claro, datos adecuados y una utilidad real para el equipo.',
  },
  {
    question: '¿Qué pasa después del lanzamiento?',
    answer: 'Seguimos siendo tu socio técnico. Acordamos el acompañamiento que necesitas y evolucionamos el sistema cuando tu operación cambia.',
  },
  {
    question: '¿Mi equipo necesita conocimientos técnicos?',
    answer: 'No. Tu equipo conoce el negocio y nosotros nos encargamos de la parte técnica. Trabajamos contigo para traducir esa experiencia en una solución clara y útil.',
  },
  {
    question: '¿Cuánto tiempo toma un proyecto?',
    answer: 'Depende del alcance que definamos en el diagnóstico. Priorizamos el proceso más crítico para que puedas ver avances sin esperar a que todo el proyecto termine.',
  },
];

export function buildHomeStructuredData(description) {
  const organization = {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Devssoft',
    url: `${SITE_URL}/`,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/brand/logotipo-navy.png`,
    },
    email: 'info@devssoft.com',
    description,
    areaServed: [
      { '@type': 'Country', name: 'Bolivia' },
      { '@type': 'Country', name: 'United States' },
    ],
    sameAs: [
      'https://www.facebook.com/devssoft',
      'https://www.linkedin.com/company/devssoftwebdevelopment',
    ],
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organization,
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: `${SITE_URL}/`,
        name: 'Devssoft',
        inLanguage: 'es',
        publisher: { '@id': ORGANIZATION_ID },
      },
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: `${SITE_URL}/`,
        name: 'Software empresarial, desarrollo a medida e IA para negocios | Devssoft',
        description,
        inLanguage: 'es',
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@id': ORGANIZATION_ID },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/brand/devssoft-og.png`,
        },
      },
      ...SERVICE_OFFERINGS.map((service) => ({
        '@type': 'Service',
        '@id': `${SITE_URL}/#service-${service.id}`,
        name: service.name,
        description: `${service.problem} ${service.features.join(', ')}.`,
        provider: { '@id': ORGANIZATION_ID },
        audience: {
          '@type': 'Audience',
          audienceType: service.audience,
        },
        areaServed: ['Bolivia', 'United States'],
      })),
      ...SOFTWARE_PRODUCTS.map((product) => ({
        '@type': 'SoftwareApplication',
        '@id': `${SITE_URL}${product.path}#software`,
        name: product.name,
        url: `${SITE_URL}${product.path}`,
        description: product.description,
        applicationCategory: product.category,
        operatingSystem: 'Web',
        author: { '@id': ORGANIZATION_ID },
        audience: {
          '@type': 'Audience',
          audienceType: product.audience,
        },
      })),
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/#faq`,
        mainEntity: FAQ_ENTRIES.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };
}
